import { useState } from "react";
import { CalculatorPage } from "../../components/CalculatorPage";
import { ResultExplainer } from "../../components/ResultExplainer";
import { FilingStatusSelector } from "../../components/FilingStatusSelector";
import { taxAPI, formatCurrency, formatPercent } from "../../lib/api";
import { buildCalculatorSchema } from "../../hooks/useSEO";
import { EmailGatePdfButton } from "../../components/EmailGatePdfButton";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const AutoLoanInterestCalculator = () => {
  const [filing, setFiling] = useState("single");
  const [interest, setInterest] = useState("4500");
  const [year, setYear] = useState("2026");
  const [isNew, setIsNew] = useState(true);
  const [magi, setMagi] = useState("75000");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await taxAPI.autoLoan({
        annual_interest: Number(interest),
        vehicle_year: Number(year),
        is_new_vehicle: isNew,
        filing_status: filing,
        magi: Number(magi),
      });
      setResult(res);
    } catch (e) {
      toast.error("Calculation failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputPanel = (
    <div className="space-y-5">
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Filing Status</label>
        <FilingStatusSelector value={filing} onChange={setFiling} testid="auto-filing" />
      </div>
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Annual Auto Loan Interest Paid</label>
        <input
          type="number"
          value={interest}
          onChange={(e) => setInterest(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-interest"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="font-body text-sm font-semibold block mb-2">Vehicle Year</label>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
            data-testid="input-year"
          />
        </div>
        <div className="flex flex-col">
          <label className="font-body text-sm font-semibold block mb-2">New Vehicle?</label>
          <label className="flex items-center gap-3 border border-foreground/40 p-3 cursor-pointer hover:bg-muted">
            <input
              type="checkbox"
              checked={isNew}
              onChange={(e) => setIsNew(e.target.checked)}
              className="w-5 h-5"
              data-testid="input-is-new"
            />
            <span className="font-body text-sm">Yes, new</span>
          </label>
        </div>
      </div>
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Modified AGI (MAGI)</label>
        <input
          type="number"
          value={magi}
          onChange={(e) => setMagi(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-magi"
        />
      </div>
      <button
        onClick={run}
        disabled={loading}
        data-testid="calculate-btn"
        className="w-full bg-primary text-primary-foreground font-mono uppercase tracking-widest text-sm px-6 py-4 hover:brightness-110 transition flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</> : "Calculate Auto Loan Deduction"}
      </button>
    </div>
  );

  const resultPanel = result ? (
    <div className="space-y-6" data-testid="auto-result">
      {!result.eligible ? (
        <div className="border-2 border-foreground bg-muted p-6" data-testid="auto-ineligible">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground block mb-2">Not Eligible</span>
          <h3 className="font-heading text-2xl font-semibold">{result.reason}</h3>
        </div>
      ) : (
        <>
          <div className="border-2 border-foreground bg-foreground text-background p-6">
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background/60 mb-2 block">Your OBBBA Auto Loan Deduction</span>
            <h3 className="font-heading text-3xl md:text-4xl font-bold leading-tight mb-2">
              You can deduct <span className="text-primary">{formatCurrency(result.eligible_deduction)}</span> of auto loan interest.
            </h3>
            <p className="font-body text-sm text-background/80 mt-3">
              Estimated federal tax savings: <span className="font-mono font-semibold">{formatCurrency(result.estimated_federal_tax_savings)}</span> at your {formatPercent(result.marginal_rate_used, 0)} marginal rate.
            </p>
          </div>
          <div className="bg-card border-2 border-foreground overflow-hidden">
            <div className="p-5 border-b border-foreground/15 grid grid-cols-2 gap-4">
              <span className="font-body text-sm">Statutory Max</span>
              <span className="font-mono tabular-nums text-right">{formatCurrency(result.max_deduction)}</span>
            </div>
            <div className="p-5 border-b border-foreground/15 grid grid-cols-2 gap-4">
              <span className="font-body text-sm">Phase-Out Threshold</span>
              <span className="font-mono tabular-nums text-right">{formatCurrency(result.phaseout_threshold)}</span>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              <span className="font-body text-sm">Phase-Out Applied</span>
              <span className={`font-mono text-right font-semibold ${result.phaseout_applied ? "text-destructive" : "text-primary"}`}>
                {result.phaseout_applied ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </>
      )}
      <div className="bg-muted border border-foreground/15 p-5">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Note</span>
        <p className="font-body text-sm">{result.note || "OBBBA's auto loan interest deduction applies only to new vehicles with final assembly in the U.S., placed in service after January 19, 2025, for personal (not business) use."}</p>
      </div>
      {result.eligible && (
        <EmailGatePdfButton
          testid="auto-pdf"
          filename="taxshift-2026-auto-loan.pdf"
          pdfPayload={{
            title: "OBBBA Auto Loan Interest Deduction",
            subtitle: `${result.inputs.vehicle_year} new vehicle · ${result.inputs.filing_status.replace("_", " ").toUpperCase()}`,
            inputs: [
              { label: "Annual Interest", value: formatCurrency(result.inputs.annual_interest) },
              { label: "Vehicle Year", value: result.inputs.vehicle_year },
              { label: "MAGI", value: formatCurrency(result.inputs.magi) },
              { label: "Filing Status", value: result.inputs.filing_status.replace("_", " ").toUpperCase() },
            ],
            headline: `You can deduct ${formatCurrency(result.eligible_deduction)} of auto loan interest — estimated federal savings ${formatCurrency(result.estimated_federal_tax_savings)}.`,
            rows: [
              { label: "Max Deduction", v2025: formatCurrency(result.max_deduction), v2026: formatCurrency(result.max_deduction) },
              { label: "Eligible Deduction", v2025: formatCurrency(result.eligible_deduction), v2026: formatCurrency(result.eligible_deduction) },
              { label: "Estimated Tax Savings", v2025: formatCurrency(result.estimated_federal_tax_savings), v2026: formatCurrency(result.estimated_federal_tax_savings) },
            ],
          }}
        />
      )}
      <ResultExplainer slug="auto-loan-interest-2026" />
    </div>
  ) : (
    <div className="border-2 border-dashed border-foreground/30 p-12 text-center bg-card/50" data-testid="auto-empty">
      <p className="font-heading text-xl">Enter your auto loan details and click Calculate.</p>
    </div>
  );

  return (
    <CalculatorPage
      title="Auto Loan Interest Deduction Calculator (OBBBA 2026)"
      slug="auto-loan-interest-2026"
      overline="Calculator · OBBBA · Auto Loan"
      description="OBBBA created a new above-the-line deduction for up to $10,000 of interest on qualified new-vehicle auto loans, for tax years 2025-2028. Applies to U.S.-assembled vehicles used for personal purposes."
      seoTitle="Auto Loan Interest Deduction Calculator 2026 — OBBBA"
      seoKeywords="auto loan interest deduction, obbba auto loan, new vehicle tax deduction, car loan tax deduction 2026"
      jsonLd={buildCalculatorSchema({
        name: "OBBBA Auto Loan Interest Deduction Calculator",
        description: "Free calculator for OBBBA's $10,000 new-vehicle auto loan interest deduction.",
        url: "/calculator/auto-loan-interest-2026",
      })}
      inputPanel={inputPanel}
      resultPanel={resultPanel}
      education={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">How the auto loan deduction works</h2>
          <div className="font-body text-base leading-relaxed space-y-4 text-foreground/85">
            <p>
              OBBBA created an above-the-line deduction allowing up to <strong>$10,000 per year</strong> of
              auto loan interest on qualifying new vehicles. Like tips and overtime deductions, it's available to both
              itemizers and non-itemizers, for tax years 2025-2028.
            </p>
            <h3 className="font-heading text-xl font-semibold mt-6">Qualification requirements</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>New vehicle</strong> — used vehicles don't qualify.</li>
              <li><strong>U.S. final assembly</strong> — the vehicle must have its final assembly in the United States.</li>
              <li><strong>Personal use</strong> — business-use vehicles are excluded (they may have other deductions).</li>
              <li><strong>Placed in service after January 19, 2025</strong>.</li>
            </ul>
            <h3 className="font-heading text-xl font-semibold mt-6">Phase-out</h3>
            <p>
              The deduction phases out ratably over a $10,000 MAGI band starting at $100,000 (single) or $200,000 (married joint).
              Above those thresholds, the deduction is reduced proportionally until fully eliminated.
            </p>
          </div>
        </>
      }
      relatedSlugs={[
        { slug: "tax-bracket-2026", label: "Tax Bracket" },
        { slug: "no-tax-on-tips", label: "No Tax on Tips" },
      ]}
    />
  );
};

export default AutoLoanInterestCalculator;
